import { useCallback, KeyboardEvent, PropsWithChildren, ReactElement, useEffect, useRef, MouseEvent } from 'react'
import { Select } from 'components/atoms/inputs/Select/parts/Select'
import { OuterEvent } from 'components/features/pfm/OuterEvent/OuterEvent'

type SelectHandlerProps<Value> = {
  context: Select<Value>
}
export const SelectHandler = <Value extends any>(props: PropsWithChildren<SelectHandlerProps<Value>>): ReactElement | null => {
  const { children, context } = props

  const {
    highlight,
    isOpen,
    selectHighlighted,
  } = context

  const handleKeyDown = useCallback<(event: KeyboardEvent<HTMLDivElement>) => void>((event) => {
    switch (event.key) {
      case 'Enter':
        if (!isOpen.visible) {
          isOpen.show()
        } else {
          selectHighlighted()
          isOpen.hide()
        }
        event.preventDefault()
        event.stopPropagation()
        return

      case 'Escape':
        if (isOpen.visible) {
          event.preventDefault()
          event.stopPropagation()
          isOpen.hide()
        }
        return

      case 'ArrowUp':
        if (isOpen.visible) {
          highlight.prev()
        }
        event.preventDefault()
        event.stopPropagation()
        return

      case 'ArrowDown':
        if (isOpen.visible) {
          highlight.next()
        } else {
          isOpen.show()
        }
        event.preventDefault()
        event.stopPropagation()
        return

    }
  }, [highlight, isOpen, selectHighlighted])

  const rootRef = useRef<HTMLDivElement>()
  const handleRef = useCallback<(ref: HTMLDivElement) => void>((ref) => {
    rootRef.current = ref
  }, [])

  useEffect(() => {
    const { current } = rootRef
    if (!current) {
      return
    }

    const handleFocus = (event: Event) => {
      const target = event.target as HTMLElement
      if (target && target !== document.body && !current.contains(target)) {
        isOpen.hide()
      }
    }
    document.body.addEventListener('focusin', handleFocus, false)

    return () => {
      document.body.removeEventListener('focusin', handleFocus, false)
    }
  })

  const handleClick = useCallback<(event: MouseEvent<HTMLDivElement>) => void>((event) => {
    const { current } = rootRef
    if (!current) {
      return
    }

    const target = event.target as HTMLElement
    const parentLabelNode = current.closest('label')
    /*
      Clicking on a menu item in a <Select> that is inside the <label> tag causes second click event
      We only need to process one of these clicks. Therefore, if there is a <label> tag, we will
        ignore all clicks except clicks by <input>
    */

    const isCorrectClick = !!parentLabelNode ?
      target.tagName.toLocaleLowerCase() === 'input' :
      true

    if (isCorrectClick) {
      isOpen.toggle()
    }
  }, [isOpen])

  return (
    <OuterEvent
      innerRef={ handleRef }
      onOuterClick={ isOpen.hide }
      onKeyDown={ handleKeyDown }
      onClick={ handleClick }
    >
      { children }
    </OuterEvent>
  )
}
